import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getMembers, DEFAULT_DIRECTOR_AVATAR, DEFAULT_LEADER_AVATAR } from '../data/membersData';
import { FaFlag, FaUsers, FaArrowTrendUp, FaHandshake, FaRocket, FaAward, FaFilter } from 'react-icons/fa6';
import { FaTools } from 'react-icons/fa';
import {
  HiCheckCircle,
  HiWrenchScrewdriver,
  HiArrowUpRight,
  HiOutlineCpuChip,
  HiOutlineBolt,
  HiOutlineSignal
} from 'react-icons/hi2';
import { MdVerified, MdGroups, MdMemory, MdSchedule, MdHandshake, MdEco } from 'react-icons/md';



/* ---- Shared Section Header ---- */
function SectionHeader({ eyebrow, title, subtitle, light = false, center = true }) {
  return (
    <div className={`mb-14 ${center ? 'text-center max-w-3xl mx-auto' : ''}`}>
      <span className="eyebrow">{eyebrow}</span>
      <h2 className={`text-3xl md:text-5xl font-display font-extrabold leading-tight mb-4 ${light ? 'text-white' : 'text-heading'}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`text-base leading-relaxed ${light ? 'text-white/65' : 'text-body-muted font-medium'}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* ---- Breadcrumb ---- */
function Breadcrumb({ crumbs }) {
  return (
    <div className="bg-white border-b border-border py-3.5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <nav className="flex items-center gap-2 text-xs text-body-muted" aria-label="Breadcrumb">
          {crumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-border-dark">/</span>}
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-primary transition-colors">{crumb.label}</Link>
              ) : (
                <span className="font-semibold text-primary">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>
    </div>
  );
}

export default function About() {
  const { hash } = useLocation();
  const [members, setMembers] = useState([]);

  useEffect(() => {
    // Scroll handling
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        const timer = setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 150);
        return () => clearTimeout(timer);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        const data = await getMembers();
        setMembers(data);
      } catch (error) {
        console.error("Failed to load members: ", error);
      }
    };
    loadMembers();
  }, []);

  const director = members.find((m) => m.type === 'director') || {
    name: 'Ashok Ravsaheb Joshi',
    role: 'Founder & Managing Director',
    tagline: '25+ Years of Industrial Energy Leadership',
    image: DEFAULT_DIRECTOR_AVATAR,
    bio: 'Ashok Ravsaheb Joshi has spent over 25 years piloting industrial electrical infrastructures and generator operations. Under his governance, Green Gusto has grown from a regional service shop into a certified multi-vertical energy firm powering national telecom networks, heavy manufacturing mills, and government grids.',
    quote: 'Our vision is not just supplying generators, it is securing continuous national growth by powering critical sectors with high reliability and zero downtime.'
  };

  const otherLeaders = members.filter((m) => m.type === 'leadership');

  // Timeline points matching 2nd image
  const timelineMilestones = [
    {
      title: 'FOUNDATION',
      desc: 'Established with a vision to provide dependable power and engineering solutions with a commitment to quality and customer satisfaction.',
      icon: <FaFlag className="w-6 h-6 text-primary" />
    },
    {
      title: 'EARLY GROWTH',
      desc: 'Built a strong foundation by understanding customer needs, delivering quality solutions, and creating a skilled and dedicated team.',
      icon: <FaUsers className="w-6 h-6 text-primary" />
    },
    {
      title: 'EXPANSION',
      desc: 'Expanded our service portfolio and strengthened our capabilities across multiple sectors, serving a diverse range of industries.',
      icon: <FaArrowTrendUp className="w-6 h-6 text-primary" />
    },
    {
      title: 'TRUST & RELATIONSHIPS',
      desc: 'Earned the trust of our clients through transparency, reliability, timely execution, and long-term partnerships.',
      icon: <FaHandshake className="w-6 h-6 text-primary" />
    },
    {
      title: 'FUTURE READY',
      desc: 'Continuing our journey with innovation, sustainability, operational excellence, and a customer-first approach.',
      icon: <FaRocket className="w-6 h-6 text-primary" />
    }
  ];

  // Strengths matching 4th image
  const ourStrengths = [
    { title: 'Quality & Reliability', desc: 'Strict compliance with quality standards, ensuring highly dependable backups and long service lifespans.', icon: <MdVerified className="w-6 h-6" /> },
    { title: 'Skilled & Experienced Team', desc: 'Direct-hire technicians and OEM-certified design engineers coordinating multi-crore infrastructure setups.', icon: <MdGroups className="w-6 h-6" /> },
    { title: 'Advanced Technology', desc: 'Integrating smart cloud telemetries, automated AMF controller grids, and highly efficient eco designs.', icon: <MdMemory className="w-6 h-6" /> },
    { title: 'Timely Execution', desc: 'SLA-driven response times and logistics grids dispatching rapid support within contract hours.', icon: <MdSchedule className="w-6 h-6" /> },
    { title: 'Strong Customer Relationships', desc: 'Transparency, fair contract compliance, and long-term partnerships representing heavy trust.', icon: <MdHandshake className="w-6 h-6" /> },
    { title: 'Commitment to Safety & Sustainability', desc: 'Prioritizing environmentally sound backup solutions, low noise pollution, and strict safety drills.', icon: <MdEco className="w-6 h-6" /> }
  ];

  // Values matching 4th image
  const valuesList = [
    'Integrity in all our actions',
    'Customer satisfaction as our priority',
    'Excellence in everything we do',
    'Teamwork and respect',
    'Innovation and continuous improvement',
    'Responsibility towards society and environment'
  ];

  // Motion variants for timeline scroller card reveal
  const timelineCardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.96 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 70,
        damping: 15,
        mass: 0.8
      }
    }
  };

  return (
    <div className="page-enter bg-light font-sans pt-16 overflow-x-hidden">

      {/* ---- Hero Banner ---- */}
      <section
        className="relative text-white py-32 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/about.png')`
        }}
      >
        {/* Decorative gold line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Corporate Profile</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            About <span className="text-gradient-gold">Green Gusto</span>
          </h1>
          <p className="text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
            A premier power engineering firm delivering turnkey silent generators, high-yield solar grids, custom panels, and SLA-driven maintenance services.
          </p>
        </div>
      </section>

      <Breadcrumb crumbs={[{ to: '/', label: 'Home' }, { label: 'About Us' }]} />

      {/* ========================================================
          SECTION 1: COMPANY PROFILE & INTRO
      ======================================================== */}
      <section id="profile" className="pt-24 pb-12 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span className="eyebrow">Introduction</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold text-heading leading-tight">
              Engineering Resilient Energy Solutions Across India
            </h2>
            <div className="space-y-4 text-sm md:text-base text-body leading-relaxed">
              <p>
                Green Gusto Energy Private Limited was established with a singular objective: to address the critical gaps in industrial and telecom backup infrastructure. We began as a regional generator maintenance workshop and evolved into a multi-vertical industrial engineering firm.
              </p>
              <p>
                Today, we handle multi-crore electrical configurations, turnkey solar EPC jobs, and nationwide telecom network backups. We do not use third-party broker networks; our technicians are directly employed and trained by major generator OEMs. This direct ownership of logistics, fleet operations, and spares helps us offer class-leading Annual Maintenance Contracts (AMC) and 24x7 emergency response guarantees.
              </p>
            </div>
            <div className="pt-4 flex flex-wrap gap-4">
              <Link to="/contact#enquiry" className="btn-primary text-sm">
                Get In Touch
                <HiArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/generator-solutions-overview" className="btn-secondary text-sm">
                Our Services
              </Link>
            </div>
          </div>

          {/* Business Overview Card */}
          <div className="lg:col-span-5 bg-charcoal text-white border border-white/5 rounded-3xl p-8 md:p-10 shadow-premium relative overflow-hidden group hover:border-primary-200 transition-all duration-300">
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl" />
            <h3 className="font-display font-bold text-xs text-accent uppercase tracking-widest mb-8 relative z-10">Business Overview</h3>
            <div className="space-y-8 relative z-10">
              {[
                { num: '01', title: 'Genset Solutions', desc: 'Turnkey silent diesel sales, mobile rental fleets (20-1500kVA), and customized AMC/CAMC plans.' },
                { num: '02', title: 'Solar EPC Division', desc: 'Design, structural calculation, and installation of rooftop grid-tied and commercial hybrid solar systems.' },
                { num: '03', title: 'Control Panelboards', desc: 'In-house assembly of custom LT/HT panelboards, AMF panels, and automatic synchronization switchgears.' },
                { num: '04', title: 'Cutting Tools', desc: 'High-performance HSS, solid carbide, and PCD tooling systems for heavy engineering and CNC machining.' },
              ].map((item) => (
                <div key={item.num} className="flex items-start gap-4 group/item">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center text-accent text-sm font-bold flex-shrink-0 group-hover/item:bg-accent group-hover/item:text-white transition-all duration-300">
                    {item.num}
                  </div>
                  <div>
                    <h4 className="text-xs md:text-sm font-bold text-white uppercase tracking-wide mb-1.5">{item.title}</h4>
                    <p className="text-xs text-white/60 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 4: LEADERSHIP & MANAGEMENT (Moved here)
      ======================================================== */}
      <section id="leadership" className="py-20 bg-[#F8FAFC] border-t border-border overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Executive Board"
            title="Corporate Leadership Team"
            subtitle="Our leadership coordinates regional deployments and high-volume project executions with absolute technical standards."
          />
        </div>

        {/* Founder / Director Spotlight — Premium Executive Layout */}
        <div className="bg-[#0C1A12] text-white border-y border-emerald-800/10 py-16 md:py-20 shadow-premium relative overflow-hidden mb-16 group transition-all duration-350">
          {/* Glowing gradient background effects */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-emerald-700/10 via-primary-dark/5 to-transparent rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-accent/5 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-emerald-900/8 rounded-full blur-[90px] pointer-events-none" />

          <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-10 lg:gap-16">
              {/* Left: Executive Photo — Reduced size */}
              <div className="w-full sm:w-80 lg:w-96 flex-shrink-0 relative z-10 flex flex-col items-center">
                {/* Profile Image */}
                <div className="relative w-full aspect-[4/5] rounded-[24px] overflow-hidden border-2 border-emerald-500/15 shadow-premium transition-all duration-500 bg-slate-900 group-hover:border-emerald-500/30">
                  <img
                    src={director.image || DEFAULT_DIRECTOR_AVATAR}
                    alt={director.name}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = DEFAULT_DIRECTOR_AVATAR;
                    }}
                  />
                  {/* Subtle gradient overlay at bottom for blending */}
                  <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0C1A12]/60 to-transparent" />
                </div>
                {/* Name & Title directly below the image */}
                <div className="mt-5 text-center">
                  <h4 className="text-xl font-display font-extrabold text-white tracking-tight">
                    {director.name}
                  </h4>
                  <p className="text-xs text-[#FFAA4D] font-bold uppercase tracking-[0.15em] mt-2">
                    {director.role}
                  </p>
                </div>
              </div>

              {/* Right: Message Content */}
              <div className="flex-1 w-full space-y-6 lg:pt-2 text-left">
                <div className="space-y-1">
                  <span className="text-xs md:text-sm text-[#FFAA4D] font-extrabold uppercase tracking-widest block">
                    Message from our Director
                  </span>
                  <div className="w-16 h-0.5 bg-[#FFAA4D]/40" />
                </div>

                {/* Bio description */}
                <p className="text-sm md:text-base text-slate-300 leading-relaxed font-medium text-justify">
                  {director.bio}
                </p>

                {/* Bio Quote */}
                {director.quote && (
                  <div className="relative bg-emerald-950/50 border border-emerald-800/25 rounded-2xl p-6 md:p-8 shadow-premium backdrop-blur-sm">
                    <span className="absolute -top-7 -left-1 text-[100px] text-emerald-800/20 font-serif select-none pointer-events-none leading-none">“</span>
                    <p className="text-base md:text-lg text-emerald-50 italic font-semibold leading-relaxed relative z-10 pl-6 border-l-[3px] border-[#FFAA4D]/70">
                      "{director.quote}"
                    </p>
                  </div>
                )}

                <div className="w-full h-px bg-gradient-to-r from-emerald-800/40 via-emerald-800/20 to-transparent" />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Sub-leadership Section (Other Leadership members under Director) */}
          <div className="border-t border-border pt-16">
            <div className="text-center mb-12">
              <span className="text-[10px] bg-primary/10 text-primary border border-primary/15 px-3.5 py-1.5 rounded-full font-extrabold tracking-widest uppercase">
                Operations & Technical Board
              </span>
              <h3 className="text-2xl md:text-3xl font-display font-extrabold text-heading mt-4">Key Leadership Members</h3>
              <p className="text-xs md:text-sm text-body-muted mt-2 font-medium">Under the guidance of the Managing Director, our department heads lead execution verticals.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherLeaders.length === 0 ? (
                <div className="col-span-full text-center py-10 text-xs text-body-muted bg-white border border-border rounded-2xl">
                  No other leadership members registered. Update via Admin console.
                </div>
              ) : (
                otherLeaders.map((leader, idx) => (
                  <div key={leader.id || idx} className="bg-white rounded-2xl border border-border shadow-card hover:shadow-premium hover-lift overflow-hidden flex flex-col group transition-all duration-300 hover:border-primary/25">
                    {/* Portrait profile aspect ratio (4/5 is standard for executive directories) */}
                    <div className="relative overflow-hidden aspect-[4/5] bg-slate-50 flex items-center justify-center border-b border-slate-100">
                      <img
                        src={leader.image || DEFAULT_LEADER_AVATAR}
                        alt={leader.name}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_LEADER_AVATAR;
                        }}
                      />
                    </div>
                    <div className="p-6 text-center space-y-2">
                      <h4 className="text-sm md:text-base font-extrabold text-heading group-hover:text-primary transition-colors">
                        {leader.name}
                      </h4>
                      <span className="inline-block text-[9px] bg-primary/5 text-primary border border-primary/10 px-3 py-1 rounded-full font-extrabold uppercase tracking-wider">
                        {leader.role}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </section>

      {/* ========================================================
          SECTION 1.5: JOURNEY & CERTIFICATIONS
      ======================================================== */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10 pt-12">
        {/* ---- Corporate Journey Timeline (Milestones matching 2nd image) ---- */}
        <div id="journey" className="mb-24 pt-8">
          <SectionHeader
            eyebrow="Our Journey"
            title="The Corporate Journey"
            subtitle="From initial milestones to present-day energy innovations, here is how we have established ourselves as an industry benchmark."
          />

          <div className="relative max-w-4xl mx-auto px-4 md:px-0">
            {/* The Vertical timeline connector line */}
            <div className="absolute left-[29px] top-4 bottom-4 w-1 bg-gradient-to-b from-[#0F5A3A] via-[#1C8C5B] to-[#F58A1F] rounded-full" />

            <div className="space-y-12">
              {timelineMilestones.map((evt, idx) => (
                <motion.div
                  key={idx}
                  variants={timelineCardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-120px" }}
                  className="relative pl-20 group flex flex-col md:flex-row gap-6 items-start"
                >
                  {/* Timeline circular badge */}
                  <div className="absolute left-[1px] top-1.5 w-14 h-14 rounded-full bg-white border-[3px] border-[#0F5A3A] flex items-center justify-center shadow-md group-hover:border-[#F58A1F] group-hover:scale-105 transition-all duration-300 z-10">
                    {evt.icon}
                  </div>

                  {/* Card Content */}
                  <div className="flex-1 bg-white rounded-2xl p-6 md:p-8 border border-border shadow-card hover:shadow-premium border-gradient-green hover:border-primary/20 transition-all duration-300">
                    <div className="flex items-center mb-3">
                      <h4 className="text-base md:text-lg font-display font-extrabold text-[#0F5A3A] uppercase tracking-wider group-hover:text-[#F58A1F] transition-colors">
                        {evt.title}
                      </h4>
                    </div>
                    <p className="text-xs md:text-sm text-body leading-relaxed font-medium">
                      {evt.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- Certifications (Premium Redesigned Layout) ---- */}
        <div className="bg-gradient-to-br from-white to-emerald-50/15 border border-border rounded-3xl p-8 md:p-12 shadow-card">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4 space-y-4">
              <span className="eyebrow text-accent">Compliance</span>
              <h3 className="text-3xl md:text-4xl font-display font-extrabold text-heading leading-tight">Certifications & Accreditations</h3>
              <p className="text-xs md:text-sm text-body-muted leading-relaxed font-semibold">
                Our machinery builds, load synchronizations, and telecom field operations are fully certified by major international standard-auditing quality bureaus and state electricity boards.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { cert: 'ISO 9001:2015', label: 'Quality Management' },
                { cert: 'ISO 14001:2015', label: 'Environmental Audit' },
                { cert: 'CPCB-IV', label: 'Emissions Standard' },
                { cert: 'Grade-A License', label: 'Supervising Board' },
              ].map(({ cert, label }) => (
                <div key={cert} className="bg-white border border-border/80 rounded-2xl p-6 text-center shadow-card hover:shadow-premium hover:border-accent/40 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between group">
                  <div className="mb-4">
                    <div className="w-10 h-10 rounded-full bg-accent/5 flex items-center justify-center mx-auto mb-4 group-hover:bg-accent group-hover:text-white text-accent transition-all duration-300">
                      <FaAward className="w-5 h-5" />
                    </div>
                    <span className="text-xs md:text-sm font-extrabold text-heading block mb-1.5">{cert}</span>
                  </div>
                  <span className="text-[9px] text-[#0F5A3A] font-extrabold uppercase tracking-widest leading-tight block pt-2 border-t border-slate-50">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 2: OUR BUSINESS DIVISIONS (3rd image)
      ======================================================== */}
      <section id="divisions-section" className="py-24 bg-gradient-to-b from-white to-light border-t border-b border-border relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Our Structure"
            title="Our Business Divisions"
            subtitle="Explore our key divisions operating through established technology partnerships and specialized corporate frameworks."
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">

            {/* Solar Solutions - ARKANNECT (Symmetric left column) */}
            <div className="bg-white border border-border shadow-card hover:shadow-premium rounded-3xl p-8 flex flex-col justify-between hover:-translate-y-1.5 transition-all duration-400 group relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-400 to-orange-600" />
              <div>
                <div className="flex flex-col gap-4 mb-6">
                  <div className="h-16 w-full flex items-center justify-start bg-slate-50 border border-slate-100 p-2.5 rounded-xl">
                    <a
                      href="https://www.arkannect.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-full flex items-center justify-start hover:opacity-80 transition-all duration-300"
                    >
                      <img
                        src="/WhatsApp Image 2026-07-02 at 5.25.04 PM.jpeg"
                        alt="ARKANNECT Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </a>
                  </div>
                  <div>
                    <span className="text-[10px] text-accent uppercase font-bold tracking-widest">Simplifying Solar</span>
                  </div>
                </div>

                <h4 className="text-lg font-bold text-heading mb-3">Solar Solutions</h4>
                <p className="text-xs md:text-sm text-body leading-relaxed mb-6 font-medium">
                  Focused on high-performance solar energy installations, including rooftop systems, on-grid/off-grid networks, and grid synchronization. Delivers efficient green energy infrastructure offset plans.
                </p>

                {/* Styled Solar Capabilities list to balance vertical height */}
                <div className="space-y-3 mt-6">
                  {[
                    { title: 'On-Grid & Off-Grid EPC', desc: 'Custom ground-mounted and rooftop commercial layouts.' },
                    { title: 'Generator Synchronization', desc: 'Intelligent integration for diesel-generator hybrid grids.' },
                    { title: 'Liaisoning & Approvals', desc: 'End-to-end net metering permissions with state boards.' }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-slate-50 border border-slate-100/80 p-3 rounded-xl flex items-start gap-3 hover:border-orange-500/20 hover:bg-orange-50/5 transition-all duration-300">
                      <HiCheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h5 className="text-[11px] font-extrabold text-slate-900 leading-snug">{item.title}</h5>
                        <p className="text-[9px] text-body-muted leading-tight mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-border flex justify-between items-center text-xs text-primary font-bold">
                <span>Solar EPC Solutions</span>
                <span className="bg-primary/5 px-3 py-1 rounded-full text-[9px] uppercase tracking-wider">Approved Partner</span>
              </div>
            </div>

            {/* Chakr Innovation Divisions (Symmetric right column with 2-column grid of spacious cards) */}
            <div className="bg-white border border-border shadow-card rounded-3xl p-8 flex flex-col justify-between hover:shadow-premium transition-all duration-400 group relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-[#1C8C5B]" />
              <div>
                <div className="flex flex-col gap-4 mb-6">
                  <div className="h-16 w-full flex items-center justify-start bg-slate-50 border border-slate-100 p-1 rounded-xl">
                    <a
                      href="https://chakr.in/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-full flex items-center justify-start hover:opacity-80 transition-all duration-300"
                    >
                      <img
                        src="/chakr Innovation.png"
                        alt="CHAKR INNOVATION Logo"
                        className="h-14 w-auto object-contain ml-2"
                      />
                    </a>
                  </div>
                  <div>
                    <span className="text-[10px] text-accent uppercase font-bold tracking-widest">CHAKR INNOVATIONS</span>
                  </div>
                </div>

                {/* Sub Divisions Grid */}
                <div className="grid grid-cols-1 gap-4">
                  {[
                    { title: 'RECD Kit', desc: 'Retrofit Emission Control Device reducing particulate emissions from diesel generator exhausts by over 90%.', icon: <FaFilter className="w-6 h-6 text-primary" /> },
                    { title: 'Pollution Regarding', desc: 'Certified emissions control technologies and compliance systems for cleaner industrial energy.', icon: <MdEco className="w-6 h-6 text-emerald-600" /> }
                  ].map((sub, idx) => (
                    <div key={idx} className="bg-light p-5 md:p-6 rounded-2xl border border-border hover:border-primary/20 hover:bg-white hover:shadow-card flex items-start gap-4 transition-all duration-300 group/sub">
                      <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm group-hover/sub:bg-primary-50 transition-colors flex-shrink-0 mt-0.5">
                        {sub.icon}
                      </div>
                      <div className="text-left">
                        <h4 className="text-xs md:text-sm font-extrabold text-slate-900 leading-snug mb-1">{sub.title}</h4>
                        <p className="text-[10px] text-body-muted leading-relaxed font-semibold">{sub.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-border flex justify-between items-center text-xs text-primary font-bold">
                <span>Power & Industrial Engineering Division</span>
                <span className="bg-primary/5 px-3 py-1 rounded-full text-[9px] uppercase tracking-wider">CPCB-IV Compliant</span>
              </div>
            </div>

            {/* TMTL-generator solution */}
            <div className="bg-white border border-border shadow-card rounded-3xl p-8 flex flex-col justify-between hover:shadow-premium transition-all duration-400 group relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-600" />
              <div>
                <div className="flex flex-col gap-4 mb-6">
                  <div className="h-16 w-full flex items-center justify-start bg-slate-50 border border-slate-100 p-2.5 rounded-xl">
                    <a
                      href="https://tmtl.in/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-full flex items-center justify-start hover:opacity-80 transition-all duration-300"
                    >
                      <img
                        src="/tmtl-logo.png"
                        alt="TMTL Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </a>
                  </div>
                  <div>
                    <span className="text-[10px] text-accent uppercase font-bold tracking-widest">Eicher Engines Power Partner</span>
                  </div>
                </div>

                {/* Sub Divisions Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Generator solutions', desc: 'High-performance silent diesel generators powered by robust TMTL (Eicher) engines.', icon: <HiOutlineCpuChip className="w-6 h-6 text-primary" /> },
                    { title: 'Telecom Services', desc: '24/7 continuous site backup power, remote fuel monitoring, and critical grid integration.', icon: <HiOutlineSignal className="w-6 h-6 text-blue-500" /> },
                    { title: 'Maintenance AMC', desc: 'OEM-certified maintenance service plans with guaranteed response SLA times.', icon: <HiCheckCircle className="w-6 h-6 text-teal-600" /> },
                    { title: 'Electrical Panel', desc: 'Custom-engineered AMF control panels, automated sync grids, and distribution switchgears.', icon: <HiOutlineBolt className="w-6 h-6 text-[#F58A1F]" /> }
                  ].map((sub, idx) => (
                    <div key={idx} className="bg-light p-5 md:p-6 rounded-2xl border border-border hover:border-primary/20 hover:bg-white hover:shadow-card flex items-start gap-4 transition-all duration-300 group/sub">
                      <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm group-hover/sub:bg-primary-50 transition-colors flex-shrink-0 mt-0.5">
                        {sub.icon}
                      </div>
                      <div className="text-left">
                        <h4 className="text-xs md:text-sm font-extrabold text-slate-900 leading-snug mb-1">{sub.title}</h4>
                        <p className="text-[10px] text-body-muted leading-relaxed font-semibold">{sub.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-border flex justify-between items-center text-xs text-primary font-bold">
                <span>Telecom & Power Solutions</span>
                <span className="bg-primary/5 px-3 py-1 rounded-full text-[9px] uppercase tracking-wider">SLA-DRIVEN</span>
              </div>
            </div>

            {/* Guring - AJ Sales */}
            <div className="bg-white border border-border shadow-card rounded-3xl p-8 flex flex-col justify-between hover:shadow-premium transition-all duration-400 group relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-purple-500 to-indigo-600" />
              <div>
                <div className="flex flex-col gap-4 mb-6">
                  <div className="h-16 w-full flex items-center justify-start gap-5 bg-slate-50 border border-slate-100 p-2.5 rounded-xl">
                    <a
                      href="https://guhring.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-full flex items-center justify-center hover:opacity-80 transition-all duration-300"
                    >
                      <img
                        src="/Guhring.png"
                        alt="Guhring Logo"
                        className="max-h-full object-contain"
                      />
                    </a>
                    <div className="w-px h-8 bg-slate-200 self-center" />
                    <a
                      href="https://www.bechemindia.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-full flex items-center justify-center hover:opacity-80 transition-all duration-300"
                    >
                      <img
                        src="/Bechem.png"
                        alt="Bechem Logo"
                        className="max-h-full object-contain"
                      />
                    </a>
                  </div>
                  <div>
                    <span className="text-[10px] text-accent uppercase font-bold tracking-widest">Tooling & Panel Dealership</span>
                  </div>
                </div>

                {/* Sub Divisions Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { title: 'AJ Sales', desc: 'Industrial sales and distribution partner for high-precision rotary tooling assemblies.', icon: <FaTools className="w-6 h-6 text-[#FFAA4D]" /> },
                    { title: 'Cutting Tools', desc: 'Premium German-engineered solid carbide, HSS, and PCD cutting and drilling tools.', icon: <HiWrenchScrewdriver className="w-6 h-6 text-emerald-600" /> },
                    { title: 'Electrical Panels', desc: 'Specialized power panels customized for high-accuracy CNC machine lines.', icon: <HiOutlineBolt className="w-6 h-6 text-[#F58A1F]" /> },
                    { title: 'Telecom Service', desc: 'Custom electrical interface panels and specialized utility connections for telecom towers.', icon: <HiOutlineSignal className="w-6 h-6 text-blue-500" /> }
                  ].map((sub, idx) => (
                    <div key={idx} className="bg-light p-5 md:p-6 rounded-2xl border border-border hover:border-primary/20 hover:bg-white hover:shadow-card flex items-start gap-4 transition-all duration-300 group/sub">
                      <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm group-hover/sub:bg-primary-50 transition-colors flex-shrink-0 mt-0.5">
                        {sub.icon}
                      </div>
                      <div className="text-left">
                        <h4 className="text-xs md:text-sm font-extrabold text-slate-900 leading-snug mb-1">{sub.title}</h4>
                        <p className="text-[10px] text-body-muted leading-relaxed font-semibold">{sub.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-border flex justify-between items-center text-xs text-primary font-bold">
                <span>Industrial Supply & Dealership</span>
                <span className="bg-primary/5 px-3 py-1 rounded-full text-[9px] uppercase tracking-wider">PRECISE TOOLING</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 3: OUR STRENGTHS & VISION/MISSION/VALUES (4th image)
      ======================================================== */}
      {/* ========================================================
          SECTION 3: OUR STRENGTHS (bg-light-section like Home.js stats)
      ======================================================== */}
      <section id="strengths" className="py-20 bg-light-section border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Why Choose Us"
            title="Our Strengths"
            subtitle="We distinguish ourselves through structural strengths, direct resource control, and verified technical compliance."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ourStrengths.map((str, idx) => (
              <div
                key={idx}
                className="bg-white border border-border rounded-2xl p-6 md:p-8 hover:-translate-y-2 hover:border-primary-light/20 shadow-card hover:shadow-premium transition-all duration-300 group"
              >
                <div className="icon-box card-icon mb-5">
                  {str.icon}
                </div>
                <h4 className="text-base font-bold text-heading uppercase tracking-wide mb-3 group-hover:text-primary transition-colors">
                  {str.title}
                </h4>
                <p className="text-xs md:text-sm text-body leading-relaxed font-medium">
                  {str.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 3.5: VISION / MISSION / VALUES
      ======================================================== */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">

          {/* Left: Vision & Mission Cards */}
          <div className="lg:col-span-7 flex flex-col justify-between gap-6">
            {/* Vision Card */}
            <div className="flex-1 bg-gradient-to-br from-white to-emerald-50/15 border-l-4 border-primary border-t border-r border-b border-border shadow-card hover:shadow-premium rounded-2xl p-8 hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300 shadow-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-display font-extrabold text-heading group-hover:text-primary transition-colors">Our Vision</h3>
              </div>
              <p className="text-xs md:text-sm text-body leading-relaxed font-semibold">
                To be one of India's most trusted and preferred energy and engineering solution providers through innovation, excellence, and sustainable growth.
              </p>
            </div>

            {/* Mission Card */}
            <div className="flex-1 bg-gradient-to-br from-white to-emerald-50/15 border-l-4 border-accent border-t border-r border-b border-border shadow-card hover:shadow-premium rounded-2xl p-8 hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-accent/5 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-all duration-300 shadow-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-display font-extrabold text-heading group-hover:text-accent transition-colors">Our Mission</h3>
              </div>
              <p className="text-xs md:text-sm text-body leading-relaxed font-semibold">
                To deliver reliable and efficient energy solutions that create value for our customers, empower our people, and contribute to a greener tomorrow.
              </p>
            </div>
          </div>

          {/* Right: Core Values Card */}
          <div className="lg:col-span-5 bg-[#0C1A12] text-white rounded-2xl p-8 border border-emerald-900/30 shadow-premium hover:border-[#FFAA4D]/25 transition-all duration-300 group relative overflow-hidden flex flex-col justify-between">
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl pointer-events-none" />
            <div>
              <div className="flex items-center gap-4 mb-8 relative z-10">
                <div className="w-12 h-12 rounded-xl bg-[#FFAA4D]/10 border border-[#FFAA4D]/25 flex items-center justify-center text-[#FFAA4D]">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className="text-xl font-display font-extrabold text-white">Our Values</h3>
              </div>

              <div className="space-y-4 relative z-10">
                {valuesList.map((val, idx) => (
                  <div key={idx} className="flex items-start gap-3.5 group/val">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-400 mt-0.5 flex-shrink-0 group-hover/val:bg-emerald-500 group-hover/val:text-white transition-all">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-xs md:text-sm text-white/80 font-bold leading-normal">
                      {val}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
}
